// Time zone configuration
const TIME_ZONE_CONFIG = {
  offsets: {
    UTC: 0, EST: -5, EDT: -4, CST: -6, CDT: -5, MST: -7, MDT: -6,
    PST: -8, PDT: -7, AKST: -9, AKDT: -8, HST: -10, HDT: -9
  },
  names: {
    UTC: "UTC", EST: "America/New_York", EDT: "America/New_York",
    CST: "America/Chicago", CDT: "America/Chicago", MST: "America/Denver",
    MDT: "America/Denver", PST: "America/Los_Angeles", PDT: "America/Los_Angeles",
    AKST: "America/Anchorage", AKDT: "America/Anchorage",
    HST: "Pacific/Honolulu", HDT: "Pacific/Honolulu"
  }
};

class CurrentDateRange extends HTMLElement {
  constructor() {
    super();
    this.render();
  }

  /**
   * Get the offset in milliseconds for a given time zone
   */
  getOffset(timeZone) {
    return (TIME_ZONE_CONFIG.offsets[timeZone] || 0) * 60 * 60000;
  }

  /**
   * Map time zone to IANA time zone name
   */
  timeZoneNameMapping(timeZone) {
    return TIME_ZONE_CONFIG.names[timeZone] || "UTC";
  }

  /**
   * Calculate date with offset considering business days and cut-off time
   */
  getDateWithOffset(days, cutOffTime, disableWeekends, timeZone) {
    const offset = this.getOffset(timeZone);
    const currentTimeZoneDate = new Date(
      new Date().getTime() + new Date().getTimezoneOffset() * 60000 + offset
    );
    
    const [cutOffHours, cutOffMinutes] = cutOffTime.split(":").map(Number);
    const cutOffTimeInMinutes = cutOffHours * 60 + cutOffMinutes;
    const currentMinutesInTimeZone = currentTimeZoneDate.getHours() * 60 + currentTimeZoneDate.getMinutes();
    
    let addDays = days;
    if (currentMinutesInTimeZone > cutOffTimeInMinutes) {
      addDays++;
    }
    
    let resultDate = new Date(currentTimeZoneDate);
    while (addDays > 0) {
      resultDate.setDate(resultDate.getDate() + 1);
      const dayOfWeek = resultDate.getDay();
      if (disableWeekends && (dayOfWeek === 0 || dayOfWeek === 6)) {
        continue;
      }
      addDays--;
    }
    
    return resultDate;
  }

  /**
   * Format date based on specified format and custom labels
   */
  formatDate(date, timeZone, dateFormat, customDayLabels = null, customMonthLabels = null, hideYear = false) {
    // Handle custom labels for non-numeric formats
    if ((customDayLabels || customMonthLabels) && dateFormat !== 'numeric') {
      return this.formatDateWithCustomLabels(date, timeZone, dateFormat, customDayLabels, customMonthLabels, hideYear);
    }
    
    // Handle ordinal format
    if (dateFormat === 'ordinal') {
      return this.formatDateWithOrdinal(date, timeZone, hideYear);
    }
    
    // Standard formatting
    const formatOptions = {
      long: {
        weekday: 'long', month: 'long', day: 'numeric',
        year: hideYear ? undefined : 'numeric',
        timeZone: this.timeZoneNameMapping(timeZone)
      },
      short: {
        month: 'short', day: 'numeric',
        year: hideYear ? undefined : 'numeric',
        timeZone: this.timeZoneNameMapping(timeZone)
      },
      numeric: hideYear 
        ? { month: '2-digit', day: '2-digit', timeZone: this.timeZoneNameMapping(timeZone) }
        : { month: '2-digit', day: '2-digit', year: 'numeric', timeZone: this.timeZoneNameMapping(timeZone) }
    };

    const options = formatOptions[dateFormat] || formatOptions.long;
    return new Intl.DateTimeFormat('en-US', options).format(date);
  }

  /**
   * Format date with ordinal suffix (e.g., "June 6th, 2025")
   */
  formatDateWithOrdinal(date, timeZone, hideYear = false) {
    const options = {
      month: "long", day: "numeric",
      year: hideYear ? undefined : "numeric",
      timeZone: this.timeZoneNameMapping(timeZone)
    };
    
    const formatter = new Intl.DateTimeFormat("en-US", options);
    const parts = formatter.formatToParts(date);
    const day = parts.find(part => part.type === "day").value;
    const dayWithOrdinal = `${day}${this.getOrdinalSuffix(day)}`;
    
    return parts
      .map(part => part.type === "day" ? dayWithOrdinal : part.value)
      .join("");
  }

  /**
   * Format date with custom day/month labels
   */
  formatDateWithCustomLabels(date, timeZone, dateFormat, customDayLabels, customMonthLabels, hideYear = false) {
    const options = {
      weekday: dateFormat === 'long' ? 'long' : undefined,
      month: dateFormat === 'short' ? 'short' : 'long',
      day: 'numeric',
      year: hideYear ? undefined : 'numeric',
      timeZone: this.timeZoneNameMapping(timeZone)
    };
    
    const formatter = new Intl.DateTimeFormat("en-US", options);
    const parts = formatter.formatToParts(date);
    
    let result = '';
    for (const part of parts) {
      if (part.type === 'weekday' && customDayLabels) {
        const dayIndex = date.getDay();
        const dayLabels = customDayLabels.split(',');
        result += dayLabels.length === 7 && dayIndex < 7 ? dayLabels[dayIndex] : part.value;
      } else if (part.type === 'month' && customMonthLabels) {
        const monthIndex = date.getMonth();
        const monthLabels = customMonthLabels.split(',');
        if (monthLabels.length === 12 && monthIndex < 12) {
          result += dateFormat === 'short' ? monthLabels[monthIndex].substring(0, 3) : monthLabels[monthIndex];
        } else {
          result += part.value;
        }
      } else if (part.type === 'day' && dateFormat === 'ordinal') {
        result += part.value + this.getOrdinalSuffix(part.value);
      } else if (part.type === 'year' && hideYear) {
        continue;
      } else {
        result += part.value;
      }
    }
    
    return result;
  }

  /**
   * Get ordinal suffix for a day (st, nd, rd, th)
   */
  getOrdinalSuffix(day) {
    const j = day % 10;
    const k = day % 100;
    if (j === 1 && k !== 11) return "st";
    if (j === 2 && k !== 12) return "nd";
    if (j === 3 && k !== 13) return "rd";
    return "th";
  }

  /**
   * Convert 24-hour time to 12-hour format with AM/PM
   */
  convertTo12HourFormat(cutOffTime) {
    let [hours, minutes] = cutOffTime.split(":").map(Number);
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    const formattedHours = hours.toString().padStart(2, '0');
    const formattedMinutes = minutes.toString().padStart(2, '0');
    return `${formattedHours}:${formattedMinutes}${ampm}`;
  }

  /**
   * Render the delivery date range component
   */
  render() {
    try {
      const settings = JSON.parse(this.dataset.settings);
      const {
        disable_weekends, start_days, end_days, truck_icon, message, cut_off_time, time_zone,
        date_format, days_labels, months_labels, hide_year, show_icon = false
      } = settings;
      
      // Calculate delivery dates
      const startDate = this.getDateWithOffset(start_days, cut_off_time, disable_weekends, time_zone);
      const endDate = this.getDateWithOffset(end_days, cut_off_time, disable_weekends, time_zone);
      
      // Format dates
      const formattedStart = this.formatDate(startDate, time_zone, date_format, days_labels, months_labels, hide_year);
      const formattedEnd = this.formatDate(endDate, time_zone, date_format, days_labels, months_labels, hide_year);
      const formattedHour = this.convertTo12HourFormat(cut_off_time);
      
      // Process message with variable substitutions
      let cleanMessage = message
        .replace(/\[start_date\]/g, formattedStart)
        .replace(/\[end_date\]/g, formattedEnd)
        .replace(/\[date\]/g, formattedStart) // For single date compatibility
        .replace(/\*date\*/g, formattedStart) // Legacy support
        .replace(/\[hour\]/g, formattedHour)
        .replace(/\*hour\*/g, formattedHour) // Legacy support  
        .replace(/\[time_zone\]/g, time_zone)
        .replace(/\*time_zone\*/g, time_zone); // Legacy support
      
      // Build icon HTML if needed
      const iconHtml = show_icon && truck_icon 
        ? `<div class="delivery-date-range-icon" part="icon">${truck_icon}</div>` 
        : '';
      
      // Render the component
      this.innerHTML = `
        <div class="delivery-date-range-container" part="container">
          ${iconHtml}
          <div class="rte" part="content">${cleanMessage}</div>
        </div>
      `;
    } catch (error) {
      console.error('Error rendering delivery date range:', error);
      this.innerHTML = '<div class="delivery-date-range-container">Error rendering delivery date</div>';
    }
  }
}

customElements.define("current-date-range", CurrentDateRange);